/**
 * Hook pour les notifications temps réel et les badges
 * Utilise WebSocket + polling fallback pour les mises à jour en temps réel
 * Avec alertes sonores pour les nouvelles demandes WhatsApp
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import notificationService from '@/services/notificationService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export function useNotifications() {
  const [badgeCounts, setBadgeCounts] = useState({
    quotes: 0,
    orders: 0,
    quote_requests: 0,
    diagnostics: 0,
    deposits: 0,
    appointments_today: 0,
    website_quotes: 0,
    total_alerts: 0
  });
  const [isConnected, setIsConnected] = useState(false);
  const [lastWhatsAppMessage, setLastWhatsAppMessage] = useState(null);
  const pollIntervalRef = useRef(null);
  const isFirstLoad = useRef(true);
  const prevQuoteRequests = useRef(0);
  const prevWebsiteQuotes = useRef(0);

  // Récupérer les compteurs via REST (fallback)
  const fetchBadgeCounts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications/badge-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newCounts = response.data;
      
      // Détecter nouvelles demandes de devis WhatsApp
      if (!isFirstLoad.current && newCounts.quote_requests > prevQuoteRequests.current) {
        // Jouer le son et afficher une notification toast
        notificationService.playNotificationSound();
        toast.success('📱 Nouvelle demande WhatsApp !', {
          duration: 8000,
          description: 'Voir dans Demandes & Devis'
        });
      }
      
      // Détecter nouvelles demandes du site web
      if (!isFirstLoad.current && newCounts.website_quotes > prevWebsiteQuotes.current) {
        notificationService.playNotificationSound();
        toast.success('🌐 Nouvelle demande du site !', {
          duration: 8000,
          description: `${newCounts.website_quotes} demande(s) en attente`
        });
      }
      
      prevQuoteRequests.current = newCounts.quote_requests;
      prevWebsiteQuotes.current = newCounts.website_quotes || 0;
      
      setBadgeCounts(prevCounts => {
        if (isFirstLoad.current || JSON.stringify(prevCounts) !== JSON.stringify(newCounts)) {
          isFirstLoad.current = false;
          return newCounts;
        }
        return prevCounts;
      });
      
    } catch (error) {
      console.error('Error fetching badge counts:', error);
    }
  }, []);

  // Initialiser WebSocket et polling
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Connecter au WebSocket
    notificationService.connect(token);

    // Écouter les événements WebSocket
    const unsubConnect = notificationService.on('connected', () => {
      setIsConnected(true);
      console.log('🔌 WebSocket connecté - notifications temps réel actives');
    });

    const unsubDisconnect = notificationService.on('disconnected', () => {
      setIsConnected(false);
    });

    const unsubBadge = notificationService.on('badge_update', (data) => {
      if (data) {
        // Détecter nouvelles demandes WhatsApp
        if (data.quote_requests > prevQuoteRequests.current && !isFirstLoad.current) {
          toast.success('📱 Nouvelle demande WhatsApp !', {
            duration: 8000,
            description: 'Voir dans Demandes & Devis'
          });
        }
        // Détecter nouvelles demandes du site web
        if ((data.website_quotes || 0) > prevWebsiteQuotes.current && !isFirstLoad.current) {
          notificationService.playNotificationSound();
          toast.success('🌐 Nouvelle demande du site !', {
            duration: 8000,
            description: 'Nouvelle demande en attente'
          });
        }
        prevQuoteRequests.current = data.quote_requests || 0;
        prevWebsiteQuotes.current = data.website_quotes || 0;
        setBadgeCounts(data);
        isFirstLoad.current = false;
      }
    });

    const unsubWhatsApp = notificationService.on('new_whatsapp_message', (data) => {
      console.log('📱 Nouveau message WhatsApp reçu:', data);
      setLastWhatsAppMessage(data);
      
      // Afficher une notification toast avec les détails
      toast.success(`📱 Message WhatsApp de ${data.client_name || 'Client'}`, {
        duration: 10000,
        description: data.message ? data.message.substring(0, 100) : 'Nouvelle demande de devis'
      });
    });

    // Fetch initial
    fetchBadgeCounts();

    // Polling fallback (15 secondes au lieu de 5)
    pollIntervalRef.current = setInterval(() => {
      fetchBadgeCounts();
    }, 15000);

    return () => {
      unsubConnect();
      unsubDisconnect();
      unsubBadge();
      unsubWhatsApp();
      notificationService.disconnect();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchBadgeCounts]);

  // Forcer une actualisation immédiate
  const refresh = useCallback(() => {
    fetchBadgeCounts();
    notificationService.refresh();
  }, [fetchBadgeCounts]);

  // Activer/désactiver le son
  const toggleSound = useCallback((enabled) => {
    notificationService.toggleSound(enabled);
  }, []);

  return {
    badgeCounts,
    isConnected,
    lastWhatsAppMessage,
    refresh,
    toggleSound
  };
}

export default useNotifications;
